import posthog from "posthog-js";
import { redactProperties } from "./redact-url";

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

if (KEY) {
  posthog.init(KEY, {
    api_host: HOST,
    // Auto-capture unhandled exceptions and unhandled promise rejections.
    // Routes through `posthog.captureException` under the hood.
    capture_exceptions: true,
    // Page views are nice to have for context on errors but the SPA doesn't
    // hard-navigate, so let posthog handle pushState transitions.
    capture_pageview: "history_change",
    // Only autocapture clicks/inputs in non-dev builds; dev creates a lot
    // of noise from React dev tooling.
    autocapture: !import.meta.env.DEV,
    persistence: "localStorage+cookie",
    // The one-time connector link's token is a credential: never send it.
    before_send: (event) => {
      if (!event) return event;
      redactProperties(event.properties);
      redactProperties(event.$set);
      redactProperties(event.$set_once);
      return event;
    },
  });
} else if (import.meta.env.PROD) {
  console.warn("[posthog] VITE_POSTHOG_KEY not set — exceptions will not be reported");
}

export { posthog };

export function identifyUser(user: { id: string; email?: string; name?: string } | null) {
  if (!KEY) return;
  if (!user) {
    posthog.reset();
    return;
  }
  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
  });
}

// Associates the current PostHog person with an organization group. Feature
// flags can then be targeted by `organization` group id / properties, letting
// us roll a feature out per-org without writing org-aware code in the app.
export function identifyOrganization(org: { id: string; name?: string } | null) {
  if (!KEY) return;
  if (!org) {
    // Drop any prior group association — usually called on sign-out.
    posthog.resetGroups();
    return;
  }
  posthog.group("organization", org.id, org.name ? { name: org.name } : {});
}
